import './RoomsMenu.scss';

import * as React from 'react';
import { useReducer } from 'react';

import { ActivityType } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import cx from 'classnames';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { sortBy } from 'underscore';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { PaywallScreens } from '~analytics/types';
import { SmallButton, UIButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';
import { IconChevron } from '~shared/components/icons/micro';
import { IconCheckmark, IconPlus } from '~shared/components/icons/standard';
import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemRenderPropArgs,
  MenuList,
} from '~shared/components/Menu';
import { Spinner } from '~shared/components/Spinner';
import { toast } from '~shared/components/Toast';
import { SmallActivityStatusIcon } from '~teacher/components/Header/ActivityStatusIcons';
import { useRoomsPaywallDispatch } from '~teacher/components/rooms/RoomsPaywall/RoomsPaywallProvider';
import { useRoomLimit } from '~teacher/components/rooms/useRoomLimit';
import { useRoomsList } from '~teacher/hook/room';
import { roomsKeys } from '~teacher/hook/room/roomsKeys';
import { queryClient } from '~teacher/queryClient';
import { selectCurrentRoomId } from '~teacher/slices/teacher/teacherSelector';
import { proCheck } from '~teacher/util/proCheck';

import { assertUnreachable } from '../../../../util/assertUnreachable';

// const require is used to import legacy code
const room = require('room');
const activity = require('activity');
const roomsController = require('../../../../../teacher/rooms/controllers/RoomsController');
const Constants = require('Constants');

export function RoomsMenu() {
  const roomQuery = useRoomsList();

  if (!roomQuery.data) {
    return <RoomsMenuLoading />;
  }

  return <RoomsMenuContent />;
}

function RoomsMenuLoading() {
  return <div data-testid={'rooms-menu'}>{translate('loading')}</div>;
}

function useCurrentRoom() {
  const roomId = useSelector(selectCurrentRoomId);
  const roomQuery = useRoomsList({
    select: (rooms) => rooms.find((room) => room.id === roomId),
  });

  return roomQuery.data;
}

function useRoomsInMenu() {
  const roomQuery = useRoomsList({
    select: (rooms) =>
      sortBy(
        rooms.filter((room) => room.inMenu),
        (room) => room.name
      ),
  });

  return roomQuery.data ?? [];
}

function RoomsMenuContent() {
  const dispatchPaywall = useRoomsPaywallDispatch();
  const roomsLimit = useRoomLimit();
  const roomsQuery = useRoomsList();
  const currentRoom = useCurrentRoom();
  const roomsInMenu = useRoomsInMenu();

  const menuListClasses = classNames(
    'rooms-menu absolute right-0 px-2 py-3 bg-white rounded-2 outline-none shadow-menu z-30 w-100 max-h-screen-minus-28 overflow-y-auto'
  );

  const [{ roomName, isOpen }, dispatch] = useReducer(reducer, {
    isOpen: false,
    roomId: null as number,
    roomName: '',
  });

  const invalidateRooms = () => {
    queryClient.invalidateQueries(roomsKeys.list());
  };

  const getRoomQuizName = (quizName: string, activityType: ActivityType) => {
    if (quizName === null) {
      return null;
    } else if (activityType === ActivityType.NoReport) {
      return translate('Quick Question');
    } else if (activityType === ActivityType.Quiz) {
      return quizName;
    }
  };

  const handleAddRoom = () => {
    try {
      if (!proCheck.canAddRoom()) {
        dispatchPaywall({
          type: 'open',
          payload: { paywallTouch: [PaywallScreens.RoomsAddRoom] },
        });
      } else if (roomsQuery.data.length === Constants.MAX_PRO_ROOMS) {
        toast(translate('You have reached the maximum number of rooms.'));
      } else if (roomsQuery.data.length === roomsLimit.data) {
        dispatchPaywall({
          type: 'open',
          payload: { paywallTouch: [PaywallScreens.RoomsMaxRooms] },
        });
      } else {
        roomsController.addRoomClicked();
        window.SOCRATIVE.teacherRouter.routeToRooms();
        new FeatureUseEventAdapter({
          feature: 'add_room',
          action: 'click',
          source: 'new_header',
        }).sendAnalytics();
      }
    } catch (e) {
      console.error('Error adding room', e);
    }
  };

  return (
    <>
      <Menu>
        <MenuButton onClick={invalidateRooms}>
          {({ props }) => (
            <SmallButton
              className={
                'rooms-menu-button max-w-60 md:max-w-80 lg:max-w-90 xl:max-w-full'
              }
              data-testid="rooms-menu-button"
              appearance={'secondary'}
              {...props}
            >
              <span className="truncate text-sm font-600">
                {currentRoom?.name?.toUpperCase() || translate('Select Room')}
              </span>
              <div className="ml-1 opacity-75 flex items-center justify-center">
                <IconChevron direction="down" />
              </div>
            </SmallButton>
          )}
        </MenuButton>
        <MenuList className={menuListClasses}>
          <MenuItem data-testid={'add-room-item'} onClick={handleAddRoom}>
            {({ isActive, props }) => (
              <RoomMenuItem
                title={translate('Add Room')}
                description={translate(
                  'Rooms allow you to launch multiple activities at once'
                )}
                isActive={isActive}
                suffix={<IconPlus />}
                {...props}
              />
            )}
          </MenuItem>
          <MenuDivider />
          <h3
            className={
              'font-sans text-xs font-700 leading-8 text-grey-900 tracking-wider uppercase mx-10 my-0 px-3 py-4'
            }
          >
            {translate('Rooms')}
          </h3>
          {roomsQuery.isFetching ? (
            <div className="my-12 mx-auto">
              <Spinner size={'small'} />
            </div>
          ) : (
            roomsInMenu.map(
              ({ name, id: roomId, quizName, activityType, state }) => (
                <MenuItem
                  data-testid={`room-${roomId}-item`}
                  key={name}
                  onClick={() => {
                    if (name === currentRoom.name) return;
                    dispatch({ type: 'open', roomName: name, roomId: roomId });
                  }}
                >
                  {({ isActive, props }) => (
                    <RoomMenuItem
                      title={name.toUpperCase()}
                      description={getRoomQuizName(quizName, activityType)}
                      isActive={isActive}
                      prefix={
                        name === currentRoom?.name ? <IconCheckmark /> : null
                      }
                      suffix={<SmallActivityStatusIcon status={state} />}
                      {...props}
                    />
                  )}
                </MenuItem>
              )
            )
          )}
        </MenuList>
      </Menu>

      <DialogConfirmation
        closeLabel={translate('Close')}
        actions={[
          <UIButton onClick={() => dispatch({ type: 'close' })} key="cancel">
            {translate('No')}
          </UIButton>,
          <UIButton
            appearance="primary"
            onClick={() => {
              /** Start of legacy handling **/
              const oldRoomName = currentRoom?.name;
              window.pubnub.unsubscribe({
                channels: [`${oldRoomName}-teacher`, `${oldRoomName}-student`],
              });

              activity.reset();

              room.join({
                role: 'teacher',
                roomname: roomName,
                success: () => {
                  window.SOCRATIVE.teacherRouter.routeToLaunch();
                  activity.refresh(() => {});
                },
                error: () => console.log('error'),
              });
              /** End of legacy handling **/

              dispatch({ type: 'close' });
            }}
            key="primary"
          >
            {translate('Yes')}
          </UIButton>,
        ]}
        open={isOpen}
        onClose={() => dispatch({ type: 'close' })}
        id={'change-room'}
        title={translate('Please Confirm')}
      >
        {translate('Are you sure you want to change to the {0} room?').format(
          roomName.toUpperCase()
        )}
      </DialogConfirmation>
    </>
  );
}
interface MenuItemProps extends MenuItemRenderPropArgs {
  title: string;
  description: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

function RoomMenuItem({
  title,
  description,
  isActive,
  prefix,
  suffix,
  ...props
}: MenuItemProps) {
  const buttonClass = classNames(
    'cursor-pointer flex flex-row  w-full p-3 whitespace-no-wrap no-underline',
    'font-sans text-base leading-8 text-left border-0 rounded-1',
    isActive ? 'bg-cyan-50' : 'bg-white',
    props['aria-disabled'] ? 'text-grey-600' : 'text-blue-900'
  );

  return (
    <>
      <button className={buttonClass} {...props}>
        <div className="w-8 h-8 mr-1">{prefix}</div>
        <div className="flex justify-between">
          <div className="flex flex-col w-64 truncate">
            <div className={cx('text-base font-sans', 'text-blue-900')}>
              <div className="truncate">{title}</div>
            </div>
            {description !== undefined && description !== null ? (
              <p className="w-64 text-grey-800 text-xs leading-6 whitespace-normal break-words font-sans text-ellipsis overflow-hidden">
                {description}
              </p>
            ) : null}
          </div>
          <div className="flex justify-center items-center w-8 h-8">
            {suffix}
          </div>
        </div>
      </button>
    </>
  );
}

type ChangeRoomConfirmationDialogAction =
  | { type: 'open'; roomId: number; roomName: string }
  | { type: 'close' };

function reducer(
  state: ChangeRoomConfirmationDialogState,
  action: ChangeRoomConfirmationDialogAction
) {
  switch (action.type) {
    case 'open': {
      const { roomId, roomName } = action;
      return {
        isOpen: true,
        roomId: roomId,
        roomName: roomName,
      };
    }
    case 'close': {
      return {
        isOpen: false,
        roomId: null as number,
        roomName: '',
      };
    }
    default:
      assertUnreachable(action);
  }
}

interface ChangeRoomConfirmationDialogState {
  isOpen: boolean;
  roomId: number;
  roomName: string;
}
